import React from "react";
import { createPortal } from "react-dom";
import { Check, CalendarDays, Users, Flag, CalendarRange, GanttChartSquare, Maximize2, X } from "lucide-react";
import { TitledPanel, Panel, EmptyState, IconTile } from "@/portal/ui/Layout";
import { Badge } from "@/portal/ui/Badge";
import { fmtDateHuman, fmtDateLong, fmtTime } from "@/portal/format";

const STAGE_RO = { Concept: "Concept", Design: "Proiectare", Tender: "Licitație & contractare", Execution: "Execuție", Handover: "Predare" };
const STAGE_SUB = { Concept: "Strategie & fezabilitate", Design: "Soluții tehnice", Tender: "Tender", Execution: "Implementare", Handover: "Recepție & operaționalizare" };

const _d = (s) => new Date(`${String(s).slice(0, 10)}T00:00:00`);
const _daysBetween = (a, b) => Math.round((_d(b) - _d(a)) / 86400000);

function ScheduleGantt({ schedule, height = 460, monthW = 84 }) {
  const tasks = (schedule?.tasks || []).filter((t) => t.start || t.finish);
  if (!tasks.length) return null;
  const starts = [], finishes = [];
  tasks.forEach((t) => { if (t.start) starts.push(t.start); if (t.finish) finishes.push(t.finish); });
  const minD = starts.reduce((m, d) => (d < m ? d : m));
  const maxD = finishes.reduce((m, d) => (d > m ? d : m));
  const total = Math.max(_daysBetween(minD, maxD), 1);
  const pct = (d) => Math.min(Math.max((_daysBetween(minD, d) / total) * 100, 0), 100);

  const months = [];
  const cur = _d(minD); cur.setDate(1);
  const end = _d(maxD);
  while (cur <= end) { months.push(new Date(cur)); cur.setMonth(cur.getMonth() + 1); }
  const MONTH_W = monthW;
  const trackW = Math.max(months.length * MONTH_W, 240);
  const LABEL_W = 220;
  const ddPct = schedule?.data_date ? pct(schedule.data_date) : null;
  const monthLabel = (dt) => dt.toLocaleDateString("ro-RO", { month: "short", year: "2-digit" });

  return (
    <div className="-mx-5 -mb-5">
      <div className="flex items-center gap-4 px-5 pb-3 text-[11px] text-cp-text-3">
        <span className="inline-flex items-center gap-1.5"><span className="h-3 w-5 rounded-sm bg-cp-navy" />Fază</span>
        <span className="inline-flex items-center gap-1.5"><span className="h-3 w-5 rounded-sm bg-cp-accent" />Activitate</span>
        <span className="inline-flex items-center gap-1.5"><span className="inline-block h-2.5 w-2.5 rotate-45 bg-cp-amber" />Jalon</span>
        {ddPct != null && <span className="inline-flex items-center gap-1.5"><span className="h-3 w-px bg-cp-danger" />Data curentă</span>}
      </div>
      <div className="overflow-x-auto">
        <div style={{ minWidth: LABEL_W + trackW }}>
          {/* Month header */}
          <div className="flex border-y border-cp-border bg-cp-bg-subtle/60 sticky top-0 z-20">
            <div style={{ width: LABEL_W }} className="shrink-0 px-5 py-2 text-[11px] font-semibold uppercase tracking-wide text-cp-text-3">Activitate</div>
            <div className="relative" style={{ width: trackW }}>
              {months.map((m, i) => (
                <div key={i} className="absolute top-0 bottom-0 border-l border-cp-border/70 px-2 py-2 text-[10.5px] text-cp-text-3 whitespace-nowrap" style={{ left: `${(i / months.length) * 100}%`, width: `${(1 / months.length) * 100}%` }}>{monthLabel(m)}</div>
              ))}
              <div className="py-2 text-[10.5px] invisible">.</div>
            </div>
          </div>
          {/* Rows */}
          <div style={{ maxHeight: height }} className="overflow-y-auto">
            {tasks.map((t) => {
              const isMs = t.kind === "milestone";
              const left = t.start ? pct(t.start) : t.finish ? pct(t.finish) : 0;
              const right = t.finish ? pct(t.finish) : left;
              const width = Math.max(right - left, isMs ? 0 : 1.4);
              const isWbs = t.kind === "wbs";
              const p = Math.min(Math.max(t.percent || 0, 0), 100);
              return (
                <div key={t.id} data-testid={`gantt-row-${t.id}`} className={`flex items-stretch border-b border-cp-border/60 hover:bg-cp-surface-active/40 ${isWbs ? "bg-cp-bg-subtle/50" : ""}`}>
                  <div style={{ width: LABEL_W }} className="shrink-0 px-5 py-2.5 flex items-center gap-1.5 min-w-0">
                    <span style={{ paddingLeft: `${(t.level - 1) * 12}px` }} className="flex items-center gap-1.5 min-w-0">
                      {isWbs ? <span className="h-2 w-2 rounded-[2px] bg-cp-navy shrink-0" /> : isMs ? <Flag className="h-3 w-3 text-cp-amber shrink-0" /> : <span className="h-1.5 w-1.5 rounded-full bg-cp-accent/60 shrink-0" />}
                      <span className={`truncate text-[12.5px] ${isWbs ? "font-semibold text-cp-text" : "text-cp-text-2"}`} title={t.name}>{t.name}</span>
                    </span>
                  </div>
                  <div className="relative shrink-0 py-2" style={{ width: trackW }}>
                    {months.map((m, i) => <div key={i} className="absolute top-0 bottom-0 border-l border-cp-border/40" style={{ left: `${(i / months.length) * 100}%` }} />)}
                    {ddPct != null && <div className="absolute top-0 bottom-0 w-[1.5px] bg-cp-danger/70 z-20" style={{ left: `${ddPct}%` }} />}
                    {isMs ? (
                      <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 z-10 h-3 w-3 rotate-45 bg-cp-amber border-2 border-white shadow" style={{ left: `${left}%` }} title={`${t.name} — ${fmtDateHuman(t.finish || t.start)}`} />
                    ) : (
                      <div className={`absolute top-1/2 -translate-y-1/2 h-[18px] rounded-[4px] overflow-hidden shadow-sm ${isWbs ? "bg-cp-navy" : "bg-cp-accent"}`}
                        style={{ left: `${left}%`, width: `${width}%` }}
                        title={`${t.name} — ${fmtDateHuman(t.start)} → ${fmtDateHuman(t.finish)}${p ? ` · ${Math.round(p)}%` : ""}`}>
                        {p > 0 && p < 100 && <div className="absolute inset-y-0 left-0 bg-black/25" style={{ width: `${p}%` }} />}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function ScheduleSection({ schedule }) {
  const [full, setFull] = React.useState(false);
  React.useEffect(() => {
    if (!full) return;
    const onKey = (e) => { if (e.key === "Escape") setFull(false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [full]);
  return (
    <>
      <TitledPanel title="Grafic de execuție" testid="schedule-gantt"
        action={
          <div className="flex items-center gap-3">
            <span className="cp-meta inline-flex items-center gap-1.5"><GanttChartSquare className="h-3.5 w-3.5" /> Primavera P6</span>
            <button type="button" onClick={() => setFull(true)} data-testid="gantt-fullscreen-btn"
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[12px] font-medium rounded-md border border-cp-border text-cp-text-2 hover:bg-cp-surface-active hover:text-cp-text transition-colors">
              <Maximize2 className="h-3.5 w-3.5" /> Ecran complet
            </button>
          </div>
        }>
        <ScheduleGantt schedule={schedule} />
      </TitledPanel>

      {full && createPortal(
        <div className="cp fixed inset-0 z-[100] flex flex-col bg-black/50 p-3 sm:p-6" role="dialog" aria-modal="true" aria-label="Grafic de execuție" data-testid="gantt-modal"
          onClick={(e) => { if (e.target === e.currentTarget) setFull(false); }}>
          <div className="rounded-[var(--cp-radius-lg)] shadow-2xl flex flex-col w-full h-full max-w-[1500px] mx-auto overflow-hidden" style={{ background: "#ffffff" }}>
            <div className="flex items-center justify-between px-5 py-3.5 border-b border-cp-border shrink-0">
              <div className="flex items-center gap-2 font-cpserif text-[16px] text-cp-text"><GanttChartSquare className="h-5 w-5 text-cp-accent" /> Grafic de execuție</div>
              <button type="button" onClick={() => setFull(false)} data-testid="gantt-modal-close" aria-label="Închide" className="cp-iconbtn"><X className="h-5 w-5" /></button>
            </div>
            <div className="flex-1 overflow-auto px-5 pt-4 pb-5">
              <ScheduleGantt schedule={schedule} height={typeof window !== "undefined" ? window.innerHeight - 210 : 700} monthW={110} />
            </div>
          </div>
        </div>, document.body)}
    </>
  );
}



export default function CalendarTab({ portal, project, milestones }) {
  const timeline = portal?.timeline || [];
  const meetings = portal?.meetings || [];
  const today = new Date().toISOString().slice(0, 10);
  const upcoming = meetings.filter((m) => m.kind !== "past" && (m.when || "").slice(0, 10) >= today).sort((a, b) => (a.when || "").localeCompare(b.when || ""));
  const next = upcoming[0];
  const completion = portal?.estimated_completion || project.end_date;
  const done = milestones.filter((m) => m.completed).length;

  return (
    <div className="grid grid-cols-1 xl:grid-cols-[1fr_320px] gap-5 items-start">
      <div className="space-y-5">
        {timeline.length > 0 && (
          <TitledPanel title="Fazele proiectului" testid="stage-tracker">
            <ol className="grid grid-cols-1 md:grid-cols-5 gap-4 md:gap-0 mt-1">
              {timeline.map((st, i) => {
                const isLast = i === timeline.length - 1;
                const cls = st.status === "Completed" ? "is-done" : st.status === "In Progress" ? "is-current" : st.status === "Delayed" ? "is-delayed" : "";
                const active = st.status === "In Progress";
                return (
                  <li key={st.stage} className="relative flex md:flex-col items-start md:items-center gap-3 md:gap-0">
                    {!isLast && <span aria-hidden="true" className={`hidden md:block absolute top-[14px] left-1/2 w-full h-px ${st.status === "Completed" ? "bg-cp-accent" : "bg-cp-border"}`} />}
                    {!isLast && <span aria-hidden="true" className="md:hidden absolute left-[13px] top-8 bottom-[-16px] w-px bg-cp-border" />}
                    <span className={`cp-step-circle ${cls}`} aria-hidden="true">{st.status === "Completed" ? <Check className="h-4 w-4" /> : i + 1}</span>
                    <div className="md:mt-2.5 md:text-center md:px-1 min-w-0">
                      <div className={`text-[13.5px] font-semibold leading-tight ${active ? "text-cp-accent" : st.status === "Completed" ? "text-cp-text" : "text-cp-text-2"}`}>{STAGE_RO[st.stage] || st.stage}</div>
                      <div className="cp-meta mt-0.5">{st.period || STAGE_SUB[st.stage] || ""}</div>
                      {active && st.progress > 0 && <div className="cp-meta mt-0.5 tabular-nums">{st.progress}% finalizat</div>}
                      {st.note && <p className="cp-meta mt-0.5">{st.note}</p>}
                    </div>
                  </li>
                );
              })}
            </ol>
          </TitledPanel>
        )}

        <TitledPanel title="Repere & termene" testid="milestones-list" action={milestones.length > 0 && <span className="cp-meta whitespace-nowrap">{done} / {milestones.length} finalizate</span>}>
          {milestones.length === 0 ? (
            <div className="text-center py-8"><CalendarRange className="h-9 w-9 mx-auto text-cp-text-3 mb-2" aria-hidden="true" /><p className="cp-h4">Reperele vor apărea aici</p><p className="cp-body-sm mt-0.5">Echipa Brisk va publica termenele proiectului.</p></div>
          ) : (
            <ol className="divide-y divide-cp-border -mx-5 -mb-5">
              {milestones.map((m, i) => (
                <li key={m.id} className="flex items-center gap-4 px-5 py-3.5" data-testid={`milestone-${m.order ?? i}`}>
                  <span className={`cp-step-circle !h-7 !w-7 text-[11px] ${m.completed ? "is-done" : ""}`} aria-hidden="true">{m.completed ? <Check className="h-3.5 w-3.5" /> : i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <div className={`text-[14px] font-medium ${m.completed ? "text-cp-text-2" : "text-cp-text"}`}>{m.title}</div>
                    {m.description && <div className="cp-meta truncate">{m.description}</div>}
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-[13px] tabular-nums text-cp-text">{fmtDateHuman(m.due_date)}</div>
                    <div className={`text-[11.5px] font-medium ${m.completed ? "text-cp-success" : "text-cp-text-3"}`}>{m.completed ? "Finalizat" : "Planificat"}</div>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </TitledPanel>

        {(portal?.schedule?.tasks || []).length > 0 && <ScheduleSection schedule={portal.schedule} />}

        {upcoming.length > 1 && (
          <TitledPanel title="Ședințe programate" testid="upcoming-meetings">
            <ul className="divide-y divide-cp-border -mx-5 -mb-5">
              {upcoming.map((m) => (
                <li key={m.id} className="flex items-center gap-4 px-5 py-3.5">
                  <IconTile icon={Users} />
                  <div className="flex-1 min-w-0"><div className="text-[14px] font-medium text-cp-text truncate">{m.title}</div>{m.agenda && <div className="cp-meta truncate">{m.agenda}</div>}</div>
                  <div className="text-right text-[13px] text-cp-text whitespace-nowrap">{fmtDateLong(m.when)}<span className="text-cp-text-2 ml-2">{fmtTime(m.when)}</span></div>
                </li>
              ))}
            </ul>
          </TitledPanel>
        )}
      </div>

      <div className="space-y-4">
        <Panel data-testid="completion-card">
          <div className="text-[13.5px] font-semibold text-cp-text">Finalizare estimată</div>
          <div className="flex items-center gap-3 mt-2"><CalendarDays className="h-7 w-7 text-cp-text-2 shrink-0" aria-hidden="true" /><span className="cp-serif text-[24px] text-cp-text leading-tight">{fmtDateLong(completion)}</span></div>
          <p className="cp-meta mt-2">Data estimată pentru finalizarea proiectului, conform planificării actuale.</p>
        </Panel>
        <Panel data-testid="next-meeting-card">
          <div className="text-[13.5px] font-semibold text-cp-text">Următoarea ședință</div>
          {next ? (
            <>
              <div className="flex items-center gap-3 mt-2"><Users className="h-7 w-7 text-cp-text-2 shrink-0" aria-hidden="true" /><div><div className="cp-serif text-[22px] text-cp-text leading-tight">{fmtDateLong(next.when)}</div><div className="cp-serif text-[18px] text-cp-text">{fmtTime(next.when)}</div></div></div>
              <p className="cp-meta mt-2">{next.title}{next.agenda ? ` — ${next.agenda}` : ""}</p>
            </>
          ) : <p className="cp-body-sm mt-2">Nicio ședință programată în perioada următoare.</p>}
        </Panel>
        {portal?.current_stage && (
          <Panel>
            <div className="flex items-center gap-2 text-[13.5px] font-semibold text-cp-text"><Flag className="h-4 w-4 text-cp-accent" aria-hidden="true" /> Etapa curentă</div>
            <p className="text-[15px] text-cp-text mt-2">{portal.current_stage}</p>
            <div className="mt-2"><Badge tone="accent" size="sm">În desfășurare</Badge></div>
          </Panel>
        )}
      </div>
    </div>
  );
}
